import * as L from 'leaflet';
import { ActiveVesselHalo } from '../shared/map-core/active-vessel-halo';
import { LOCATED_COLOR, locatedIconSpec } from '../shared/models/located.model';
import { VesselFinderVesselRow, vesselRowHasFix } from './vesselfinder-map.controller';
import { buildVesselFinderPopup } from './vessel-popup';
import { VesselFinderPopupActions } from './vessel-popup.types';

export interface VfLocatedLayerOptions {
  /** Prices (and kept-ness) for this row's popup buttons — sensor-scoped, resolved by the facade. */
  actionsFor: (row: VesselFinderVesselRow) => VesselFinderPopupActions;
  /** A popup button was clicked — the facade owns what each action does (fetch, save, remove). */
  onAction: (action: string, row: VesselFinderVesselRow) => void;
  /**
   * A hull became the ACTIVE vessel (marker clicked, or picked from a list) —
   * or null when it was deselected. Mirrors Voyage Radar's
   * `SelectionController.activateVessel`: the facade points the port-calls
   * drawer at whichever row this reports, for free (no fetch).
   */
  onActivate?: (row: VesselFinderVesselRow | null) => void;
}

/**
 * VesselFinder's marker layer — one amber diamond per located/searched hull.
 *
 * Deliberately NOT the shared `ais/shared/map-core/located.layer.ts`: that
 * class needs a full `AisMapContext` (display/selection/snapshot ports) to
 * decide ring-vs-diamond against a drawn AOI snapshot. VesselFinder draws no
 * snapshot at all (module header: "no map facade… located markers only"), so
 * every marker here is always the plain diamond glyph — no twin logic, no
 * `AisMapContext` needed.
 *
 * The popup itself IS the shared one, though — same `ais-vessel-popup`
 * Leaflet popup class and `ais-popup__*` card Voyage Radar/AISstream use (see
 * `vessel-popup.ts`), so the card looks identical across every AIS provider.
 */
/**
 * Two classes, not one: `ais-vessel-popup` is the shared card chrome every AIS
 * provider mounts through, and `ais-vessel-popup--vf` is the hook for the one
 * rule that cannot be shared — this is the only card whose whole body scrolls,
 * so it is the only one that needs `.leaflet-popup-content`'s right margin
 * handed to the scroll box (see `styles.scss`). Leaflet splices `className`
 * straight into the container's class string, so a space-separated pair works.
 */
const VF_POPUP_OPTIONS: L.PopupOptions = {
  className: 'ais-vessel-popup ais-vessel-popup--vf',
  closeButton: false,
  offset: L.point(0, -6),
};

/**
 * How long an OUTCOME note ("nothing returned", "could not load") stays up.
 * `_notes` is keyed by hull and nothing else ever clears it, so without this a
 * card reopened days later still greets the user with the last attempt's
 * failure. Progress notes ("Loading…") are not auto-cleared — the request that
 * put them up takes them down.
 */
const NOTE_TTL_MS = 6000;

export class VfLocatedLayer {
  private _layer: L.LayerGroup | null = null;
  private _markers = new Map<string, L.Marker>();
  private _rows = new Map<string, VesselFinderVesselRow>();
  /** Transient per-hull message (loading, refusal, failure) — never cleared by `sync`, which would erase it mid-fetch. */
  private _notes = new Map<string, string>();
  /** Pending auto-clear per hull, so a newer note (or a removed marker) cancels the older one's expiry. */
  private _noteTimers = new Map<string, any>();
  /** The hull whose card is open, kept across redraws so a purchase cannot close it. */
  private _openKey: string | null = null;
  /** Map-level listeners are wired once, lazily — the map does not exist when this class is built. */
  private _mapWired = false;
  /** The vessel a click (or a list pick) last selected, or null. */
  private _primaryActiveKey: string | null = null;
  /** All vessels currently activated with halo rings (e.g. search / expected arrival results). */
  private _activeKeys = new Set<string>();
  /** Pulsing selection ring on the active hull(s) — the shared primitive Voyage Radar's own selection also draws through. */
  private readonly halo = new ActiveVesselHalo();

  constructor(
    private readonly host: { map: L.Map | null },
    private readonly opts: VfLocatedLayerOptions,
  ) {}

  /** `imo` reaches this class as a raw number from the backend's own normalizer, so never trust the declared string type. */
  private key(r: VesselFinderVesselRow): string {
    return String(r.mmsi ?? r.imo ?? '');
  }

  /** Redraw from the merged list — located wins a key collision (fuller record). */
  sync(searchResults: VesselFinderVesselRow[] = [], locatedVessels: VesselFinderVesselRow[] = []): void {
    const map = this.host.map;
    if (!map) return;
    this._wireMap(map);
    this._sync(map, searchResults, locatedVessels);
    this.ensurePopupOpen();
  }

  /**
   * Puts the tracked card back up if anything took it down.
   *
   * Leaflet closes a marker's popup whenever the marker leaves the map, and a
   * redraw detaches markers for reasons that have nothing to do with the user
   * — buying Master/Voyage re-syncs this hull the instant it lands. Rather than
   * chase every such path, the open card is tracked here and restored after
   * one, so the data appears in the card the click was made in.
   */
  ensurePopupOpen(key?: string): void {
    const k = key == null ? this._openKey : String(key);
    if (!k) return;
    this._openKey = k;
    const marker = this._markers.get(k);
    if (marker && !marker.isPopupOpen()) marker.openPopup();
  }

  /** Clicking the water dismisses the card for good — a later redraw must not bring it back. */
  private _wireMap(map: L.Map): void {
    if (this._mapWired) return;
    this._mapWired = true;
    map.on('click', () => {
      this._openKey = null;
    });
  }

  private _sync(map: L.Map, searchResults: VesselFinderVesselRow[], locatedVessels: VesselFinderVesselRow[]): void {
    if (!this._layer || !map.hasLayer(this._layer)) {
      this._layer = L.layerGroup().addTo(map);
    }

    const merged = new Map<string, VesselFinderVesselRow>();
    for (const r of locatedVessels) {
      const k = this.key(r);
      if (k) merged.set(k, r);
    }
    for (const r of searchResults) {
      const k = this.key(r);
      if (k) {
        const existing = merged.get(k);
        if (existing) {
          merged.set(k, {
            ...existing,
            ...r,
            stale: false,
            master: r.master ?? existing.master,
            voyage: r.voyage ?? existing.voyage,
            portCalls: r.portCalls ?? existing.portCalls,
            kept: existing.kept ?? r.kept,
          });
        } else {
          merged.set(k, r);
        }
      }
    }

    for (const [k, marker] of Array.from(this._markers.entries())) {
      if (!merged.has(k)) {
        this._layer.removeLayer(marker);
        this._markers.delete(k);
        this._rows.delete(k);
        this._activeKeys.delete(k);
        if (this._primaryActiveKey === k) {
          this._primaryActiveKey = Array.from(this._activeKeys)[0] ?? null;
        }
      }
    }

    merged.forEach((row, k) => {
      const existing = this._markers.get(k);
      // A watched hull with no stored position (common now that Watched
      // Vessels seeds straight from `vesselHistory`, not only from a live
      // fetch that always had one) stays off the map rather than drawing at
      // 0,0 — see `vesselRowHasFix` for why the null check cannot be skipped.
      const lat = Number((row as any).latitude);
      const lng = Number((row as any).longitude);
      const hasFix = vesselRowHasFix(row);

      if (!hasFix) {
        // Master/Voyage responses carry no position, so this is the ONLY path a
        // bought dataset takes to an already-drawn card — returning outright (as
        // this did) left that card showing its pre-purchase row forever.
        if (existing) {
          this._rows.set(k, row);
          existing.getPopup()?.setContent(this._popupHtml(k, row));
        }
        return;
      }

      this._rows.set(k, row);
      const icon = L.divIcon(locatedIconSpec({ cog: (row as any).course, heading: (row as any).heading, timeUtc: row.timeUtc }, false) as any);
      const html = this._popupHtml(k, row);

      if (existing) {
        // Re-add unconditionally (idempotent) — `resetMapNew()` sweeps every
        // non-tile layer off the map, including `_layer`, and the NEXT sync
        // rebuilds a FRESH layer group here without touching `_markers`, so a
        // marker tracked from before the sweep is a live object that is no
        // longer attached to anything. Without this line it silently sat off
        // the map forever: still "found" by `centerVessel`/popup lookups, but
        // `panTo`/`openPopup` on a marker with no `_map` do nothing — this was
        // why Center on Vessel (and reaching the Watch button behind it)
        // stopped working after the first task-switch/reset.
        this._layer!.addLayer(existing);
        existing.setLatLng([lat, lng]);
        existing.setIcon(icon);
        const popup = existing.getPopup();
        if (popup) popup.setContent(html);
        else existing.bindPopup(html, VF_POPUP_OPTIONS);
        // The active hull's ring rides its marker across every redraw, the same
        // way Voyage Radar's own halo follows a re-anchored selection.
        if (this._activeKeys.has(k) && this.host.map) this.halo.moveTo(this.host.map, existing.getLatLng(), LOCATED_COLOR, k);
        return;
      }

      const marker = L.marker([lat, lng], { icon, keyboard: false } as any);
      marker.bindPopup(html, VF_POPUP_OPTIONS);
      marker.on('click', () => this.activate(k));
      marker.on('popupopen', (e: any) => {
        this._openKey = k;
        this._wirePopup(e, k);
      });
      this._layer!.addLayer(marker);
      this._markers.set(k, marker);
    });

    const haloPositions = new Map<string, L.LatLngExpression>();
    this._activeKeys.forEach((ak) => {
      const m = this._markers.get(ak);
      if (m) haloPositions.set(ak, m.getLatLng());
    });
    if (this.host.map) {
      this.halo.syncMultiple(this.host.map, haloPositions, LOCATED_COLOR);
    }
  }

  private _popupHtml(key: string, row: VesselFinderVesselRow): string {
    return buildVesselFinderPopup(row, {
      ...this.opts.actionsFor(row),
      note: this._notes.get(key) ?? null,
    });
  }

  /**
   * Show (or clear) a transient message on one hull's card, refreshing it even
   * while open.
   *
   * `autoClear` marks a note as an OUTCOME rather than progress: it is taken
   * down again after `NOTE_TTL_MS` unless something newer replaced it first.
   */
  setNote(key: string, note: string | null, opts: { autoClear?: boolean } = {}): void {
    const k = String(key ?? '');
    if (!k) return;

    this._clearNoteTimer(k);
    if (note) this._notes.set(k, note);
    else this._notes.delete(k);
    this._renderNote(k);

    if (!note || !opts.autoClear) return;
    this._noteTimers.set(
      k,
      setTimeout(() => {
        this._noteTimers.delete(k);
        // A newer note took this hull over in the meantime — leave it alone.
        if (this._notes.get(k) !== note) return;
        this._notes.delete(k);
        this._renderNote(k, true);
      }, NOTE_TTL_MS),
    );
  }

  /**
   * Repaint one hull's card for a note change.
   *
   * Leaflet's `setContent` replaces the content node's innerHTML outright, so
   * the `.ais-popup--vf` scroll box is rebuilt and lands back at the top —
   * which is what we want when a note APPEARS (it renders under the header, so
   * the user sees it without scrolling). When one silently expires, though,
   * yanking the card back to the top under a reader is the opposite of what
   * they asked for, so that path carries the scroll offset across.
   */
  private _renderNote(k: string, preserveScroll = false): void {
    const row = this._rows.get(k);
    const popup = this._markers.get(k)?.getPopup();
    if (!row || !popup) {
      this.ensurePopupOpen();
      return;
    }
    const before = preserveScroll
      ? (popup.getElement()?.querySelector('.ais-popup--vf') as HTMLElement | null)?.scrollTop ?? 0
      : 0;
    popup.setContent(this._popupHtml(k, row));
    if (before) {
      const after = popup.getElement()?.querySelector('.ais-popup--vf') as HTMLElement | null;
      if (after) after.scrollTop = before;
    }
    this.ensurePopupOpen();
  }

  /**
   * Scroll the open card to the section an action just produced.
   *
   * Every bought dataset renders BELOW the hull's own rows, and `setContent`
   * rebuilds the scroll box at the top — so on a card with a full row set the
   * answer to the click landed under the fold and the only visible change was
   * the "Loading…" note disappearing. That reads as "nothing happened", which
   * is exactly wrong for a click that just spent credits.
   *
   * Matched on the section's own heading text rather than an index, because
   * which sections are present depends on what this hull has been bought
   * before: "Port calls" is the third block on one card and the first on
   * another. No match (the empty-result paths, which raise a note instead of a
   * section) is a no-op, not a guess.
   */
  revealSection(key: string, title: string): void {
    const box = this._markers.get(String(key ?? ''))?.getPopup()?.getElement()
      ?.querySelector('.ais-popup--vf') as HTMLElement | null;
    if (!box) return;

    const want = title.trim().toLowerCase();
    const heading = Array.from(box.querySelectorAll('.ais-popup__section-title')).find(
      (el) => (el.textContent ?? '').trim().toLowerCase() === want,
    ) as HTMLElement | undefined;
    if (!heading) return;

    const section = heading.parentElement ?? heading;
    // Measured rather than read off `offsetTop`: that is relative to the nearest
    // POSITIONED ancestor, which is the box only as long as `.ais-popup` keeps
    // its `position: relative`. Rects need no such assumption. The 8px lead-in
    // keeps the section's own top border on screen.
    const delta = section.getBoundingClientRect().top - box.getBoundingClientRect().top;
    box.scrollTop = Math.max(0, box.scrollTop + delta - 8);
  }

  /**
   * Delegated once per popup DOM element (not per button) so it survives a
   * later `setContent` call — e.g. buying Master data updates the SAME open
   * popup's markup, and the container node itself is reused underneath.
   */
  private _wirePopup(e: any, key: string): void {
    const container: any = e?.popup?.getElement?.();
    if (!container || container.__vfWired) return;
    container.__vfWired = true;
    container.addEventListener('click', (ev: Event) => {
      // A click inside the card is never a map click — and saying so HERE, not
      // by leaning on Leaflet's own guard, is what keeps the popup open.
      //
      // Leaflet's `disableClickPropagation` does not stop `click`; it flags the
      // popup container with `_leaflet_disable_click` and has `Map._handleDOMEvent`
      // walk `parentNode` up from the event target to find the flag. Every
      // handler below repaints the card synchronously (`setNote` →
      // `popup.setContent` → `_contentNode.innerHTML = …`), which DETACHES the
      // clicked element while its own event is still bubbling — so that walk
      // dead-ends at `null`, the flag is never found, and the map takes the
      // click as its own. It then fires the synthetic `preclick` that Leaflet
      // binds to `Popup.close` (`closePopupOnClick`, on by default), and the
      // card the user just bought data in shuts under them.
      ev.stopPropagation();

      const target = ev.target as HTMLElement;
      if (target?.closest?.('.ais-popup__close')) {
        this._openKey = null;
        this.host.map?.closePopup();
        return;
      }
      const btn = target?.closest?.('[data-card-action]') as HTMLElement | null;
      if (!btn) return;
      const action = btn.getAttribute('data-card-action');
      const row = this._rows.get(key);
      if (action && row) this.opts.onAction(action, row);
    });
  }

  centerVessel(key: string): void {
    const k = String(key ?? '');
    this._openKey = k;
    let marker = this._markers.get(k);
    if (marker && this.host.map) {
      this.host.map.panTo(marker.getLatLng());
      marker.openPopup();
      this.activate(k);
      setTimeout(() => {
        const m = this._markers.get(k);
        if (m && !m.isPopupOpen()) {
          m.openPopup();
        }
      }, 50);
    } else {
      setTimeout(() => {
        marker = this._markers.get(k);
        if (marker && this.host.map) {
          this.host.map.panTo(marker.getLatLng());
          marker.openPopup();
          this.activate(k);
        }
      }, 50);
    }
  }

  /** Is this hull currently drawn? — the sidebar history panel's "ON MAP" tag. */
  hasMarker(key: string): boolean {
    return this._markers.has(String(key ?? ''));
  }

  removeMarker(key: string): void {
    const k = String(key ?? '');
    const marker = this._markers.get(k);
    if (marker && this._layer) this._layer.removeLayer(marker);
    this._markers.delete(k);
    this._rows.delete(k);
    this._activeKeys.delete(k);
    this._clearNoteTimer(k);
    this._notes.delete(k);
    if (this._openKey === k) this._openKey = null;
    if (this._primaryActiveKey === k) {
      this._primaryActiveKey = Array.from(this._activeKeys)[0] ?? null;
      if (!this._activeKeys.size) this.clearActive();
    }
  }

  clear(): void {
    this._layer?.clearLayers();
    this._markers.clear();
    this._rows.clear();
    this._noteTimers.forEach((t) => clearTimeout(t));
    this._noteTimers.clear();
    this._notes.clear();
    this._openKey = null;
    this.clearActive();
  }

  private _clearNoteTimer(k: string): void {
    const pending = this._noteTimers.get(k);
    if (pending) {
      clearTimeout(pending);
      this._noteTimers.delete(k);
    }
  }

  /** The selected vessel's key, or null — read by the facade for the popup-less "which vessel" question. */
  activeKey(): string | null {
    return this._primaryActiveKey;
  }

  /**
   * Selects `key` — halo ring on its marker, popup already opening on its own
   * (Leaflet's default click behaviour on a marker with a bound popup). Mirrors
   * Voyage Radar's `SelectionController.activateVessel`: this is what gives the
   * port-calls drawer a subject with no extra click.
   */
  activate(key: string): void {
    const k = String(key ?? '');
    const marker = this._markers.get(k);
    if (!marker) return;
    this._primaryActiveKey = k;
    this._activeKeys = new Set([k]);
    if (this.host.map) {
      const positions = new Map<string, L.LatLngExpression>();
      positions.set(k, marker.getLatLng());
      this.halo.syncMultiple(this.host.map, positions, LOCATED_COLOR);
    }
    this.opts.onActivate?.(this._rows.get(k) ?? null);
  }

  /**
   * Activates multiple hulls (e.g. all Expected Arrivals results) with halo rings,
   * fitting bounds if multiple positions exist.
   */
  activateAll(keys?: string[]): void {
    const targetKeys = keys && keys.length ? keys : Array.from(this._markers.keys());
    this._activeKeys = new Set(targetKeys);
    this._primaryActiveKey = targetKeys[0] ?? null;
    const positions = new Map<string, L.LatLngExpression>();
    const latLngs: L.LatLng[] = [];
    for (const k of targetKeys) {
      const marker = this._markers.get(k);
      if (marker) {
        const ll = marker.getLatLng();
        positions.set(k, ll);
        latLngs.push(ll);
      }
    }
    if (this.host.map) {
      this.halo.syncMultiple(this.host.map, positions, LOCATED_COLOR);
      if (latLngs.length > 1) {
        const bounds = L.latLngBounds(latLngs);
        this.host.map.fitBounds(bounds, { padding: [50, 50], maxZoom: 12 });
      } else if (latLngs.length === 1) {
        this.host.map.panTo(latLngs[0]);
      }
    }
    const primaryRow = this._primaryActiveKey ? this._rows.get(this._primaryActiveKey) ?? null : null;
    this.opts.onActivate?.(primaryRow);
  }

  /** Takes the halo off the map and clears the selection — the marker/popup underneath is untouched. */
  private clearActive(): void {
    if (!this._activeKeys.size) return;
    this._activeKeys.clear();
    this._primaryActiveKey = null;
    this.halo.remove(this.host.map);
    this.opts.onActivate?.(null);
  }
}
